
"use client";

import { TalkingAnimationType } from "../../types/AvatarContext";
import { Modality, Type } from "@google/genai";
import { useEffect, useRef, useState, useMemo } from "react";
import { VISEMES } from "wawa-lipsync";
import { AudioRecorder } from "../lib/AudioRecorder";
import { KrpanoCanvasCapture } from "../lib/krpano-canvas-capture";
import { AudioStreamer } from "../lib/AudioStreamer";
import { GeminiClient } from "../lib/GeminiClient";
import { visualCaptureDiagnostic } from "../lib/visual-capture-diagnostic";
import { activeVisualSourceRegistry, type VisualSourceCaptureOptions, type VisualSourceCaptureResult } from "@/templates/Heritage/components/ModelLoader/spatial/visual-analysis-contract";
import { MODEL_VISUAL_GROUNDING_INSTRUCTION, VISUAL_ANALYSIS_INSTRUCTION, FULL_360_CAPABILITY_INSTRUCTION, buildKrpanoSpatialContextText } from "../lib/model-visual-context";
import { handleToolCall, tools, portalTools, mapModeTools, executeAndCoordinateToolCalls, GeminiTool, clearPendingKnowledgeSearches } from "../lib/tools";
import { getSystemInstruction } from "@/agents/systemInstrctions/agentInstructions";
import { RecommendationPopup } from "./RecommendationPopup";
import FacilityDemoOverlay from "./FacilityDemoOverlay";
import { getSkinnedMeshRenderer } from "../../skinnedMesh/getSkinnedMeshRenderer";
import { useIdPath } from "@/contexts/IdPathProvider";
import { useModules } from "@/contexts/ModuleProvider";
import AgentChatInput from "@/components/AgentChatInput/AgentChatInput";
import AgentKnowledgeCheckpointHud from "@/components/AgentChatInput/agent-knowledge-checkpoint-hud";
import AgentRecordButton from "@/utils/web-recording/AgentRecordButton";
import voiceFillerEngine from "@/agents/shared/voiceFillerEngine";
import { useLanguage } from "@/contexts/I18nProvider";
import { useLanguages } from "@/contexts/LanguageContext";
import { useSession } from "next-auth/react";
import { getAgentDataWithCustomConfig } from "@/utils/tools/agents/agentUtils";
import generalAgentService from '@/templates/Starglobal3D/components/GeneralAgent/services/generalAgentService';

import functionCallingServices from '@/utils/services/functionCalling';
import { AvatarPlayer } from "@/lib/3d-avatar-module/index.mjs";
import { isLowPowerDevice } from "@/utils/is-low-power-device";
import { useKrpanoInteracting } from "@/utils/use-krpano-interacting";
import { FaPhone, FaPhoneSlash, FaTimes, FaHourglassHalf, FaPlay, FaPause } from 'react-icons/fa';

const GEMINI_LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  eng: "English",
  us: "English",
  vn: "Vietnamese",
  vi: "Vietnamese",
  vie: "Vietnamese",
  vietnamese: "Vietnamese",
  "tiếng việt": "Vietnamese",
  fr: "French",
  fra: "French",
  fre: "French",
  de: "German",
  deu: "German",
  ger: "German",
  es: "Spanish",
  spa: "Spanish",
  zh: "Chinese",
  zho: "Chinese",
  chi: "Chinese",
  km: "Khmer",
  khm: "Khmer",
};

function resolveGeminiDefaultLanguage(locale?: string, languageName?: string): string {
  const normalizedLocale = locale?.trim().toLowerCase();
  if (normalizedLocale && GEMINI_LANGUAGE_NAMES[normalizedLocale]) {
    return GEMINI_LANGUAGE_NAMES[normalizedLocale];
  }

  const normalizedName = languageName?.trim().toLowerCase();
  if (normalizedName && GEMINI_LANGUAGE_NAMES[normalizedName]) {
    return GEMINI_LANGUAGE_NAMES[normalizedName];
  }

  return languageName?.trim() || locale?.trim() || "Vietnamese";
}

function getGeminiLanguageDirective(defaultLanguage: string): string {
  return `LANGUAGE DIRECTIVE: Start this session in ${defaultLanguage}. Keep responding in ${defaultLanguage} until the user changes the project language or speaks to you in another language. If the user speaks another language, detect it silently and continue in that language.`;
}

/** Extract agent name from the first "Bạn (tên) là X" or "You are X" line in a system prompt. */
function extractNameFromSystemPrompt(prompt: string): string | null {
  const match = prompt.match(/(?:Bạn(?:\s+tên)?\s+là|You are)\s+([^,\.\n]+)/i);
  return match ? match[1].trim() : null;
}

export const ANIMATION_DURATIONS: Record<TalkingAnimationType, number> = {
  'Talking_0': 8.226666450500488,
  'Talking_1': 4.12666654586792,
  'Talking_2': 4.760000228881836,
}

// Default fallback config when API has not responded yet
const DEFAULT_AGENT_CONFIG = {
  name: 'Thúy Kiều',
  models: '/models/thuy_kieu_ao_dai_v2.gltf',
  animations: '/models/animation_thuy_kieu.glb',
  meshPreset: 'thuykieu' as string,
  meshConfig: undefined as any,
  voice: 'aoede',
  systemPrompt: '',
  avatar: undefined as any,
  enableChatInput: false as boolean,
  searchMode: 'hybrid' as string,
  enableVisualCapture: false,
  enableFull360Capture: false,
};

// Map frequency bins to visemes roughly
const mapFrequencyToViseme = (frequencyData: Uint8Array): string | null => {
  // Simple energy detection for now
  const sum = frequencyData.reduce((a, b) => a + b, 0);
  const average = sum / frequencyData.length;

  if (average < 10) return null; // Silence

  // Randomize viseme based on intensity or simple mapping for demo
  // A proper solution connects wawa-lipsync to the audio source directly
  // But wawa-lipsync usually expects an AudioElement or a MediaStream

  // Since we have an Analyser, we can just randomly pick "talking" visemes
  // if volume is above threshold, or try to be smarter.

  // Let's use a simple mapping for "talking" movement
  const talkingVisemes = Object.values(VISEMES);
  return talkingVisemes[Math.floor(Math.random() * talkingVisemes.length)];
};

interface GeminiAvatarProps {
  isOpen?: boolean;
  isMapMode?: boolean;
  isHubMode?: boolean;
  hubTools?: Record<string, GeminiTool>;
  hubSystemInstruction?: string;
  /** Custom opening message for hub mode. If not provided, falls back to a generic greeting. */
  hubOpeningMessage?: string;
  onClose?: () => void;
  onCloseChatbot?: () => void;
  modelPath?: string;
  animationPath?: string;
  skinnedMesh?: any;
  voice?: string;
}

export default function GeminiAvatar({
  isOpen,
  isMapMode = false,
  isHubMode = false,
  hubTools: hubToolsProp,
  hubSystemInstruction,
  hubOpeningMessage,
  onClose,
  onCloseChatbot,
  modelPath,
  animationPath,
  skinnedMesh,
  voice
}: GeminiAvatarProps = {}) {
  const handleClose = onClose || onCloseChatbot;
  const { idPath, projectValue } = useIdPath();
  const { activeModules } = useModules();

  const isKnowledgeBaseEnabled = useMemo(() => {
    if (!activeModules) return false;
    return Array.isArray(activeModules)
      ? (activeModules as any).includes('aiKnowledgeBase')
      : Boolean(activeModules['aiKnowledgeBase']);
  }, [activeModules]);

  useEffect(() => {
    if (idPath && typeof window !== 'undefined') {
      (window as any).id_path = idPath;
    }
  }, [idPath]);
  const { locale } = useLanguage();
  const { data: session } = useSession();
  const sessionUserId = useMemo(() => {
    return (session as any)?.userData?.id || (session as any)?.userData?.user_id || (session?.user as any)?.id || (session?.user as any)?.user_id || null;
  }, [session]);
  const { languages, currentLanguage } = useLanguages();
  const selectedLanguage = useMemo(() => {
    return languages.find((language) =>
      language.id?.toLowerCase() === locale?.toLowerCase() ||
      language.code?.toLowerCase() === locale?.toLowerCase()
    );
  }, [languages, locale]);
  const defaultLanguage = useMemo(() => {
    const currentLanguageMatchesLocale =
      currentLanguage?.id?.toLowerCase() === locale?.toLowerCase() ||
      currentLanguage?.code?.toLowerCase() === locale?.toLowerCase();

    return resolveGeminiDefaultLanguage(
      locale,
      currentLanguageMatchesLocale ? currentLanguage?.name : selectedLanguage?.name
    );
  }, [locale, currentLanguage?.id, currentLanguage?.code, currentLanguage?.name, selectedLanguage?.name]);

  // Dynamic agent config from Admin
  const [agentConfig, setAgentConfig] = useState(DEFAULT_AGENT_CONFIG);
  const [isLoadingAgent, setIsLoadingAgent] = useState(true);

  const isChatInputEnabled = useMemo(() => {
    // 1. Phai duoc kich hoat module trong Nang cao > Bat tat chuc nang truoc (Tier 1 Gate)
    const hasModule = Array.isArray(activeModules)
      ? (activeModules as any).includes('agentChatInput')
      : Boolean(activeModules?.['agentChatInput']);
    if (!hasModule) return false;

    // 2. Chat input khong bi tat trong cau hinh Agent / Co so tri thuc (Tier 2 Preference)
    return agentConfig?.enableChatInput !== false;
  }, [activeModules, agentConfig?.enableChatInput]);

  const isAiInteractionRecordingEnabled = useMemo(() => {
    if (!activeModules) return true;
    if (Array.isArray(activeModules)) {
      return (activeModules as any).includes('aiInteractionRecording');
    }
    return Boolean(activeModules['aiInteractionRecording']);
  }, [activeModules]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__aiInteractionRecordingEnabled = isAiInteractionRecordingEnabled;
    }
  }, [isAiInteractionRecordingEnabled]);

  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState<string>("Disconnected");

  // Đồng bộ cờ toàn cục __aiAgentActive và ngắt toàn bộ âm thanh nền khi AI Agent kết nối
  useEffect(() => {
    const isAgentConnected = status === "Connected";
    if (typeof window !== "undefined") {
      (window as any).__aiAgentActive = isAgentConnected;
      if (isAgentConnected && typeof (window as any).silenceAllNonTourAudio === "function") {
        (window as any).silenceAllNonTourAudio();
      }
    }
    return () => {
      if (typeof window !== "undefined") {
        (window as any).__aiAgentActive = false;
      }
    };
  }, [status]);
  const [visemeOverride, setVisemeOverride] = useState<string | undefined>(undefined);
  const [talkingAnimation, setTalkingAnimation] = useState<TalkingAnimationType>("Talking_1");
  const [isThinking, setIsThinking] = useState(false);
  const [isPreloaded, setIsPreloaded] = useState(false);
  const [lowPower] = useState(isLowPowerDevice);
  const avatarPaused = useKrpanoInteracting(lowPower);
  const [isChatExpanded, setIsChatExpanded] = useState(false);
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);
  // Mirrors isAvatarTalking so the lipsync loop can skip redundant setState calls without a stale closure.
  const isAvatarTalkingRef = useRef(false);
  isAvatarTalkingRef.current = isAvatarTalking;
  const talkingDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const isMapModeRef = useRef(isMapMode);
  const isReconnectingRef = useRef(false);
  const isManualDisconnectRef = useRef(false);
  const isPausedRef = useRef(false);
  const isToolRunningRef = useRef(false);
  const isThinkingRef = useRef(false);
  const lastActiveMapModeRef = useRef(isMapMode);
  const agentConfigRef = useRef(agentConfig);
  const defaultLanguageRef = useRef(defaultLanguage);
  const lastActiveDefaultLanguageRef = useRef(defaultLanguage);
  const hubOpeningMessageRef = useRef(hubOpeningMessage);

  useEffect(() => {
    isMapModeRef.current = isMapMode;
  }, [isMapMode]);

  useEffect(() => {
    agentConfigRef.current = agentConfig;
  }, [agentConfig]);

  useEffect(() => {
    defaultLanguageRef.current = defaultLanguage;
  }, [defaultLanguage]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  // Listen for agent changes from Tour Mode selection modal or localStorage
  const [selectedAgentCode, setSelectedAgentCode] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('sg3d_selected_tour_voice') || localStorage.getItem('selected_agent') || localStorage.getItem('agent') || '';
    }
    return '';
  });

  useEffect(() => {
    const handleAgentChanged = (e: any) => {
      const newAgent = e?.detail?.agentCode || (typeof window !== 'undefined' ? (localStorage.getItem('sg3d_selected_tour_voice') || localStorage.getItem('selected_agent') || localStorage.getItem('agent')) : null);
      if (newAgent) {
        setSelectedAgentCode(newAgent);
      }
    };

    window.addEventListener('sg3d_agent_changed', handleAgentChanged);
    window.addEventListener('storage', handleAgentChanged);
    return () => {
      window.removeEventListener('sg3d_agent_changed', handleAgentChanged);
      window.removeEventListener('storage', handleAgentChanged);
    };
  }, []);

  // Fetch dynamic agent config from Admin "AI Agent 3D"
  useEffect(() => {
    const fetchConfig = async () => {
      setIsLoadingAgent(true);
      try {
        let config;
        let agentCode;

        if (idPath) {
          const savedAgent = selectedAgentCode || (typeof window !== 'undefined' ? (localStorage.getItem('sg3d_selected_tour_voice') || localStorage.getItem('selected_agent') || localStorage.getItem('agent')) : null);
          agentCode = savedAgent || projectValue?.agent?.toString() || 'thuykieuaodai';
          config = await getAgentDataWithCustomConfig(agentCode, idPath);
        } else {
          config = await generalAgentService.getGlobalConfig();
          agentCode = config.agentCode;
        }
        
        const nameFromPrompt = config.systemPrompt ? extractNameFromSystemPrompt(config.systemPrompt) : null;
        const resolvedConfig = {
          name: nameFromPrompt || config.avatar?.name || config.name || agentCode,
          models: modelPath || config.models || config.modelPath || DEFAULT_AGENT_CONFIG.models,
          animations: animationPath || config.animations || config.animationPath || DEFAULT_AGENT_CONFIG.animations,
          meshPreset: config.meshPreset || DEFAULT_AGENT_CONFIG.meshPreset,
          meshConfig: config.meshConfig,
          voice: voice || config.voice || config.voiceType || DEFAULT_AGENT_CONFIG.voice,
          systemPrompt: config.systemPrompt || '',
          avatar: skinnedMesh || config.avatar || config.skinnedMesh,
          enableChatInput: config.enableChatInput !== undefined ? config.enableChatInput : false,
          searchMode: config.searchMode || 'hybrid',
          enableVisualCapture: !!config.enableVisualCapture,
          enableFull360Capture: !!config.enableFull360Capture,
        };
        setAgentConfig(resolvedConfig);
      } catch (error) {
        console.error('Failed to fetch agent config, using defaults:', error);
        setAgentConfig(DEFAULT_AGENT_CONFIG);
      } finally {
        setIsLoadingAgent(false);
      }
    };
    fetchConfig();
  }, [idPath, projectValue?.agent, modelPath, animationPath, voice, skinnedMesh, selectedAgentCode]);

  // Disconnect voice/socket connection when the chatbot is closed
  useEffect(() => {
    if (isOpen === false && isActive) {
      console.log("Disconnecting Gemini Live session because agent is closed");
      setStatus("Disconnecting...");
      audioRecorderRef.current?.stop();
      audioStreamerRef.current?.stop();
      clientRef.current?.disconnect();
      capturerRef.current?.dispose();
      capturerRef.current = null;
      // hotspotHighlighter.dispose();
      setIsActive(false);
      setIsConnecting(false);
      setIsThinking(false);
      setIsPaused(false);
      setStatus("Disconnected");
      setVisemeOverride(undefined);
    }
  }, [isOpen, isActive]);

  const activeModelPath = agentConfig.models;
  const activeAnimationPath = agentConfig.animations;

  // Resolve SkinnedMeshRenderer based on meshPreset from Admin config
  const resolvedSkinnedMesh = useMemo(() => {
    // If the agent config has a specific React component for rendering this exact model, prioritize it.
    if (agentConfig.avatar) {
      const Renderer = (nodes: any, materials: any, props: any, group: any) => {
        const renderedContent = agentConfig.avatar(nodes, materials, props, undefined);
        return (
          <group {...props} dispose={null} ref={group}>
            {renderedContent}
          </group>
        );
      };
      Renderer.displayName = 'SkinnedMeshRenderer';
      return Renderer;
    }
    return getSkinnedMeshRenderer(agentConfig.meshPreset, agentConfig.meshConfig);
  }, [agentConfig.meshPreset, agentConfig.meshConfig, agentConfig.avatar]);

  useEffect(() => {
    let active = true;
    setIsPreloaded(false);

    const preloadAssets = async () => {
      try {
        await Promise.all([
          fetch(activeModelPath, { method: 'GET', mode: 'cors' }).then(r => r.blob()),
          fetch(activeAnimationPath, { method: 'GET', mode: 'cors' }).then(r => r.blob())
        ]);
        if (active) {
          setIsPreloaded(true);
        }
      } catch (err) {
        console.error("Failed to preload avatar assets:", err);
        if (active) {
          setIsPreloaded(true);
        }
      }
    };

    preloadAssets();

    return () => {
      active = false;
    };
  }, [activeModelPath, activeAnimationPath]);

  const clientRef = useRef<GeminiClient | null>(null);
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const audioStreamerRef = useRef<AudioStreamer | null>(null);
  const isAgentSpeakingRef = useRef(false);
  const capturerRef = useRef<KrpanoCanvasCapture | null>(null);
  const isModelCaptureInFlightRef = useRef(false);
  const unregisterKrpanoSourceRef = useRef<(() => void) | null>(null);

  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Lipsync tracking refs
  const currentVisemeRef = useRef<string | undefined>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const updateThrottleMs = 250; // 250ms throttle to prevent rapid updates

  // Animation tracking refs
  const currentAnimationStartTimeRef = useRef<number>(Date.now());
  const currentAnimationRef = useRef<TalkingAnimationType>("Talking_1");

  // Constants
  const apiKey = "dummy-key-to-bypass-sdk-checks";
  const MODEL = "gemini-3.1-flash-live-preview";


  // Update ref when animation changes
  useEffect(() => {
    if (talkingAnimation !== currentAnimationRef.current) {
      currentAnimationRef.current = talkingAnimation;
      currentAnimationStartTimeRef.current = Date.now();
    }
  }, [talkingAnimation]);

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    clientRef.current = new GeminiClient(apiKey, idPath || (window as any)?.id_path, sessionUserId);
    if (typeof window !== 'undefined') {
      (window as any).__geminiClient = clientRef.current;
      (window as any).__geminiClientRef = clientRef.current;
    }

    // Setup audio streamer
    // Don't use the async helper from utils as it waits for resume() which blocks without user interaction
    const rec = (typeof window !== 'undefined' && (window as any).tourRecorder) || null;
    const audioCtx = rec?.getOrCreateAudioContext?.() || new (window.AudioContext || (window as any).webkitAudioContext)();
    audioStreamerRef.current = new AudioStreamer(audioCtx);
    audioStreamerRef.current.onComplete = () => {
      isAgentSpeakingRef.current = false;
      setVisemeOverride(undefined);
    };
    analyserRef.current = audioStreamerRef.current.analyser;
    try {
      if (typeof window !== 'undefined' && (window as any).tourRecorder) {
        (window as any).tourRecorder.registerAudioSource(audioStreamerRef.current.analyser);
      }
    } catch {}

    const client = clientRef.current;

    const onOpen = () => {
      console.log("Connection opened");
      setStatus("Connected");
      setIsActive(true);
      setIsConnecting(false);
      isReconnectingRef.current = false;
      isManualDisconnectRef.current = false;
    };

    const onClose = (e?: any) => {
      console.log("Connection closed", e?.code, e?.reason);
      if (e?.code === 4402 || e?.reason?.includes("AI_TOKEN_EXHAUSTED") || e?.reason?.includes("exhausted")) {
        isReconnectingRef.current = false;
        setStatus("Trợ lý AI tạm dừng do hết token. Vui lòng nạp thêm token để tiếp tục.");
        setIsActive(false);
        setIsConnecting(false);
        setIsThinking(false);
        audioRecorderRef.current?.stop();
        audioStreamerRef.current?.stop();
        setVisemeOverride(undefined);
        return;
      }

      if (isReconnectingRef.current) {
        console.log("Ignoring onClose because we are reconnecting");
        return;
      }
      
      if (!isManualDisconnectRef.current && clientRef.current?.resumptionHandle) {
        console.log("Connection closed unexpectedly but resumption handle exists. Attempting auto-reconnect...");
        isReconnectingRef.current = true;
        // Wait a short delay before reconnecting to avoid tight loops
        setTimeout(() => {
          startVoice(true).catch((err) => {
            // Reconnect failed — likely token expired (valid for 2hr per Google Docs).
            // Clear stale handle so next connect starts a fresh session.
            console.error("Auto-reconnect failed:", err);
            isReconnectingRef.current = false;
            setStatus("Disconnected");
            setIsActive(false);
            setIsConnecting(false);
            setIsThinking(false);
            audioRecorderRef.current?.stop();
            audioStreamerRef.current?.stop();
            setVisemeOverride(undefined);
          });
        }, 1000);
        return;
      }

      setStatus("Disconnected");
      setIsActive(false);
      setIsConnecting(false);
      setIsThinking(false);
      audioRecorderRef.current?.stop();
      audioStreamerRef.current?.stop();
      setVisemeOverride(undefined);
    };

    const onError = (error: ErrorEvent | any) => {
      console.error("Connection error:", error);
      if (isReconnectingRef.current) {
        console.log("Ignoring onError because we are reconnecting");
        return;
      }
      const rawMsg = String(error?.message || "");
      let friendlyStatus = "Không thể kết nối với trợ lý AI";
      if (rawMsg.includes("4402") || rawMsg.includes("402") || rawMsg.includes("AI_TOKEN_EXHAUSTED") || rawMsg.toLowerCase().includes("hết token")) {
        friendlyStatus = "Trợ lý AI tạm dừng do hết token. Vui lòng nạp thêm token để tiếp tục.";
        isReconnectingRef.current = false;
      } else if (rawMsg.toLowerCase().includes("payment")) {
        friendlyStatus = "Trợ lý AI hiện đang tạm nghỉ. Vui lòng quay lại sau.";
      } else if (rawMsg.includes("503") || rawMsg.toLowerCase().includes("unavailable")) {
        friendlyStatus = "Dịch vụ AI đang bảo trì. Vui lòng thử lại sau.";
      }
      setStatus(friendlyStatus);
      setIsActive(false);
      setIsConnecting(false);
      setIsThinking(false);
      audioRecorderRef.current?.stop();
      audioStreamerRef.current?.stop();
      if (talkingDebounceTimerRef.current) {
        clearTimeout(talkingDebounceTimerRef.current);
        talkingDebounceTimerRef.current = null;
      }
      setIsAvatarTalking(false);
      setVisemeOverride(undefined);
    };

    const onAudio = (data: ArrayBuffer) => {
      // Discard incoming audio from server while paused to prevent
      // buffered playback when resuming
      if (isPausedRef.current) return;
      isAgentSpeakingRef.current = true;
      if (!isToolRunningRef.current) {
        voiceFillerEngine.cancel();
      }
      audioStreamerRef.current?.addPCM16(new Uint8Array(data));
    };

    const onInterrupted = () => {
      isThinkingRef.current = false;
      isAgentSpeakingRef.current = false;
      isToolRunningRef.current = false;
      clearPendingKnowledgeSearches();
      voiceFillerEngine.cancel();
      audioStreamerRef.current?.stop();
      if (talkingDebounceTimerRef.current) {
        clearTimeout(talkingDebounceTimerRef.current);
        talkingDebounceTimerRef.current = null;
      }
      setIsAvatarTalking(false);
      setVisemeOverride(undefined);
    };

    const onThinking = (thinking: boolean) => {
      isThinkingRef.current = thinking;
      // Suppress thinking state changes while paused — avatar stays idle
      if (isPausedRef.current) return;
      setIsThinking(thinking);
      if (thinking) {
        setStatus("Thinking...");
      }
      // Don't set status here when thinking ends - let onTurnComplete handle it
    };

    const onTurnComplete = () => {
      isThinkingRef.current = false;
      // Suppress turn-complete status updates while paused
      if (isPausedRef.current) return;
      // Model finished responding, tell audio streamer no more chunks are coming
      audioStreamerRef.current?.complete();
      // Note: we don't reset isAgentSpeakingRef here. The streamer will fire onComplete when the buffer empties.
      setStatus("Active - Listening...");
      setVisemeOverride(undefined);
      if (talkingDebounceTimerRef.current) {
        clearTimeout(talkingDebounceTimerRef.current);
        talkingDebounceTimerRef.current = null;
      }
      setIsAvatarTalking(false);
    };

    const onToolCall = async (toolCall: any) => {
      console.log("Tool call received", toolCall);
      const functionCalls = toolCall.functionCalls;
      if (!functionCalls || functionCalls.length === 0) return;
      isToolRunningRef.current = true;

      try {
        // When paused, don't execute any tool calls — respond with a no-op
        // so the server doesn't hang waiting for a tool response.
        // The conversation context is preserved; tools just won't fire.
        if (isPausedRef.current) {
          console.log("⏸️ Tool call blocked (session paused):", functionCalls.map((fc: any) => fc.name));
          const noopResponses = functionCalls.map((fc: any) => ({
            name: fc.name,
            response: { result: "Session is paused — tool call skipped" },
            id: fc.id,
          }));
          client.sendToolResponse({ functionResponses: noopResponses });
          return;
        }

        // Select the right tool set based on mode: Hub > Map > Project > Portal
        const resolveTools = () => {
          const base = (isHubMode && hubToolsProp)
            ? hubToolsProp
            : isMapModeRef.current
              ? mapModeTools
              : ((idPath || (window as any)?.id_path) ? tools : portalTools);

          return isKnowledgeBaseEnabled
            ? base
            : Object.fromEntries(Object.entries(base).filter(([k]) => 
                k !== 'search_project_knowledge' && 
                k !== 'get_search_result' && 
                k !== 'search_whitelist_web'
              ));
        };

        await executeAndCoordinateToolCalls({
          functionCalls,
          client,
          getCurrentClient: () => clientRef.current,
          toolRegistry: resolveTools(),
          handleToolCall,
          getActiveModelId: () => activeVisualSourceRegistry.getActiveModelId(),
          hasModelViewerMounted: () => activeVisualSourceRegistry.hasModelViewerMounted(),
        });
      } finally {
        // After full-360 capture, keep tool-busy a bit longer so spokeRecently()
        // cannot push a narrow FOV frame over the panorama before the first answer.
        const hadFull360 = functionCalls.some((fc: any) => fc?.name === 'capture_full_scene');
        setTimeout(() => {
          isToolRunningRef.current = false;
        }, hadFull360 ? 2500 : 800);
      }
    };

    const onSetupComplete = () => {
      console.log("Setup complete - sending opening message");

      const isProjectMode = !!(idPath || (window as any)?.id_path);

      // Use ref to get current avatar name (avoids stale closure from useEffect)
      const avatarName = agentConfigRef.current.name;
      const sessionLanguage = defaultLanguageRef.current;
      const openingLanguageRule = getGeminiLanguageDirective(sessionLanguage);

      // Gửi tin nhắn ban đầu để kích hoạt AI chào
      if (clientRef.current?.status === "connected") {
        console.log("Sending opening message with avatar and project info");
        if (isHubMode) {
          const hubGreeting = hubOpeningMessageRef.current ||
            `You are starting/resuming a session on the main homepage portal. Greet the user warmly as ${avatarName}, introduce yourself naturally as the General virtual 3D assistant of Starglobal 3D on the main portal. Mention company capabilities in 3D scanning, smart tourism, and space digitization, and offer to show them sectors or answer general questions.`;
          clientRef.current.send([
            {
              text: `${openingLanguageRule}\n\n${hubGreeting}`
            }
          ], true);
        } else if (isProjectMode) {
          clientRef.current.send([
            {
              text: `${openingLanguageRule}\n\nYou are starting a new session. Greet the user warmly as ${avatarName} (a knowledgeable local tour guide). ONLY ONCE AT THE VERY BEGINNING, call the list_scene_names tool to get the scene names, pick 2-3 highlights, and welcome the user to explore them. Once you have greeted the user and mentioned the highlights, STOP and wait for their questions. Do NOT repeat the scene introduction in subsequent turns. Answer their questions simply, concisely, and naturally.`
            }
          ], true);
        } else if (isMapModeRef.current) {
          clientRef.current.send([
            {
              text: `${openingLanguageRule}\n\nYou are now assisting the user in the 3D Map view (Bản đồ số hóa). Greet the user warmly as ${avatarName}, and let them know you can help them filter projects by region, province, or business sector directly on this interactive map (for example, finding heritage projects in Hà Nội or tourism in Central region). Keep it brief, natural, and friendly.`
            }
          ], true);
        } else {
          clientRef.current.send([
            {
              text: `${openingLanguageRule}\n\nYou are starting/resuming a session on the main homepage portal. Greet the user warmly as ${avatarName}, introduce yourself naturally as the General virtual 3D assistant of Starglobal 3D on the main portal. Mention company capabilities in 3D scanning, smart tourism, and space digitization, and offer to show them sectors or answer general questions.`
            }
          ], true);
        }
      }
    };

    client.on("open", onOpen);
    client.on("setupcomplete", onSetupComplete);
    client.on("close", onClose);
    client.on("error", onError);
    client.on("audio", onAudio);
    client.on("interrupted", onInterrupted);
    client.on("thinking", onThinking);
    client.on("turncomplete", onTurnComplete);
    client.on("toolcall", onToolCall);

    // Lipsync Loop
    // Reused across frames (no per-frame allocation) and sampled at ~30fps: lipsync
    // does not need display refresh rate, and this loop shares the main thread with krpano.
    let freqBuffer: Uint8Array<ArrayBuffer> | null = null;
    let lastLipsyncSample = 0;
    const LIPSYNC_SAMPLE_INTERVAL_MS = 33;
    const updateLipsync = () => {
      const now = Date.now();
      if (now - lastLipsyncSample < LIPSYNC_SAMPLE_INTERVAL_MS) {
        animationFrameRef.current = requestAnimationFrame(updateLipsync);
        return;
      }
      lastLipsyncSample = now;
      if (analyserRef.current && audioStreamerRef.current) {
        if (!freqBuffer || freqBuffer.length !== analyserRef.current.frequencyBinCount) {
          freqBuffer = new Uint8Array(analyserRef.current.frequencyBinCount);
        }
        const dataArray = freqBuffer;
        analyserRef.current.getByteFrequencyData(dataArray);

        const viseme = mapFrequencyToViseme(dataArray);

        // Smooth body posture management: Maintain Talking posture across micro-pauses (1200ms debounce)
        if (viseme) {
          if (talkingDebounceTimerRef.current) {
            clearTimeout(talkingDebounceTimerRef.current);
            talkingDebounceTimerRef.current = null;
          }
          // Only touch React state on a real transition, not every sample.
          if (!isAvatarTalkingRef.current) {
            setIsAvatarTalking(true);
          }
        } else if (!viseme && !talkingDebounceTimerRef.current && isAvatarTalkingRef.current) {
          talkingDebounceTimerRef.current = setTimeout(() => {
            setIsAvatarTalking(false);
            talkingDebounceTimerRef.current = null;
          }, 1200);
        }

        // Only update React state if viseme changed AND enough time has passed (throttle)
        if (viseme !== currentVisemeRef.current && (now - lastUpdateTimeRef.current) > updateThrottleMs) {
          currentVisemeRef.current = viseme || undefined;
          lastUpdateTimeRef.current = now;
          setVisemeOverride(viseme || undefined);

          // Maintain steady, natural upright posture (Talking_1) to prevent leg jitter/spreading
          if (talkingAnimation !== "Talking_1") {
            setTalkingAnimation("Talking_1");
          }
        } else if (!viseme && currentVisemeRef.current) {
          // Clear viseme when audio stops
          if ((now - lastUpdateTimeRef.current) > updateThrottleMs) {
            currentVisemeRef.current = undefined;
            lastUpdateTimeRef.current = now;
            setVisemeOverride(undefined);
          }
        }
      }
      animationFrameRef.current = requestAnimationFrame(updateLipsync);
    };
    updateLipsync();

    return () => {
      client.off("open", onOpen);
      client.off("close", onClose);
      client.off("error", onError);
      client.off("audio", onAudio);
      client.off("interrupted", onInterrupted);
      client.off("thinking", onThinking);
      client.off("turncomplete", onTurnComplete);
      client.off("toolcall", onToolCall);
      client.disconnect();
      audioRecorderRef.current?.stop();
      audioStreamerRef.current?.stop();
      capturerRef.current?.dispose();
      capturerRef.current = null;
      unregisterKrpanoSourceRef.current?.();
      unregisterKrpanoSourceRef.current = null;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [apiKey]);

  const startVoice = async (isReconnecting = false, enableMic = true) => {
    if (!clientRef.current || !audioStreamerRef.current) {
      setStatus("Error: Client not initialized");
      return;
    }

    setIsConnecting(true);
    setStatus(isReconnecting ? "Reconnecting..." : "Connecting...");

    clientRef.current.disconnect();

    try {
      // Setup audio recorder
      audioRecorderRef.current?.stop();
      audioRecorderRef.current = new AudioRecorder(16000);

      // Setup visual capture if enabled in admin config or in debug mode
      capturerRef.current?.dispose();
      capturerRef.current = null;
      unregisterKrpanoSourceRef.current?.();
      unregisterKrpanoSourceRef.current = null;
      const isVisualCaptureEnabled = visualCaptureDiagnostic.isCaptureEnabled(!!agentConfigRef.current.enableVisualCapture);
      if (isVisualCaptureEnabled || visualCaptureDiagnostic.isDebugActive()) {
        const krpano = new KrpanoCanvasCapture({ maxSize: 512, throttleMs: 1000, krpanoElementId: 'pano' });
        capturerRef.current = krpano;
        unregisterKrpanoSourceRef.current = activeVisualSourceRegistry.registerSource({
          sourceId: 'krpano',
          captureForQuestion: async (options?: VisualSourceCaptureOptions): Promise<VisualSourceCaptureResult> => {
            const capture = await krpano.captureWhenReady();
            if (capture) {
              return {
                success: true,
                turnId: options?.turnId,
                requestId: options?.requestId,
                snapshot: {
                  schemaVersion: '1.0',
                  capturedAt: capture.timestamp,
                  image: {
                    data: capture.imageBase64,
                    mimeType: 'image/jpeg',
                    width: 512,
                    height: 512,
                    encodedBytes: capture.imageBase64.length,
                  },
                  spatialContext: {
                    schemaVersion: '1.0',
                    source: 'krpano',
                    capturedAt: capture.timestamp,
                    sceneId: (typeof window !== 'undefined' && window?.id_path) ? window.id_path : null,
                    modelId: null,
                    focusedObjectId: capture.spatialContext?.focusedObject?.name ?? null,
                    visibleObjectIds: capture.spatialContext?.visibleObjects.map(h => h.name) ?? [],
                    camera: {
                      type: 'krpano',
                      hlookat: capture.camera.hlookat,
                      vlookat: capture.camera.vlookat,
                      fov: capture.camera.fov,
                    },
                  },
                },
              };
            }
            return {
              success: false,
              skippedReason: 'rate-limited',
              turnId: options?.turnId,
              requestId: options?.requestId,
            };
          },
          isActive: () => !!capturerRef.current,
        });
        console.log('[GeminiAvatar] Visual capture enabled — frames sent only when user speaks (3s grace window)');
      }

      audioRecorderRef.current.on("start", () => {
        console.log("AudioRecorder start emitted");
        if (clientRef.current?.status === "connected") {
          clientRef.current.startThinking();
        }
      });

      audioRecorderRef.current.on("data", (base64Audio: string) => {
        // Guard: don't send audio when paused — mic tracks are muted but
        // the worklet may still fire buffered frames briefly
        if (clientRef.current?.status === "connected" && !audioRecorderRef.current?.isPaused) {
          // Audio chunk is ALWAYS sent immediately — completely non-blocking
          const chunks: Array<{ mimeType: string; data: string }> = [
            {
              mimeType: "audio/pcm;rate=16000",
              data: base64Audio,
            },
          ];

          // Check visual capture source if enabled
          const captureAllowed = visualCaptureDiagnostic.isCaptureEnabled(!!agentConfigRef.current.enableVisualCapture);
          const isModelMounted = activeVisualSourceRegistry.hasModelViewerMounted();
          const activeSource = activeVisualSourceRegistry.getActiveSource();

          const selectedSourceId = activeSource
            ? activeSource.sourceId
            : (isModelMounted ? 'three-model-viewer' : 'none');
          const selectedModelId = activeSource?.sourceId === 'three-model-viewer'
            ? (activeSource as any).modelId
            : (isModelMounted ? activeVisualSourceRegistry.getActiveModelId() : null);

          visualCaptureDiagnostic.setSelectedSource(selectedSourceId, selectedModelId);

          if (!captureAllowed) {
            visualCaptureDiagnostic.recordSkipped({
              sourceId: selectedSourceId,
              reason: 'disabled',
            });
            // Visual capture disabled — still send audio so Gemini can hear the user
            clientRef.current.sendRealtimeInput(chunks);
          } else if (isModelMounted) {
            if (activeSource?.sourceId === 'three-model-viewer') {
              // 3D Model Viewer is active: capture asynchronously without delaying audio transmission
              // Audio is ALWAYS sent immediately — image capture happens independently
              clientRef.current.sendRealtimeInput(chunks);

              // Only capture when user spoke recently AND agent is NOT speaking
              // (sending images while agent speaks causes Gemini to interrupt its own response)
              const userSpokeForModel = !isAgentSpeakingRef.current && (audioRecorderRef.current?.spokeRecently(3000) ?? false);
              if (!isModelCaptureInFlightRef.current && userSpokeForModel) {
                isModelCaptureInFlightRef.current = true;
                visualCaptureDiagnostic.recordRequest('three-model-viewer');

                const captureClient = clientRef.current;
                const captureSession = captureClient.session;

                activeSource
                  .captureForQuestion()
                  .then((res) => {
                    if (
                      res.success &&
                      res.snapshot?.image?.data &&
                      clientRef.current?.status === 'connected' &&
                      clientRef.current === captureClient &&
                      captureClient.session === captureSession &&
                      activeVisualSourceRegistry.getActiveSource() === activeSource &&
                      visualCaptureDiagnostic.isCaptureEnabled(!!agentConfigRef.current.enableVisualCapture)
                    ) {
                      const imgData = res.snapshot.image.data;
                      const modelId = res.snapshot.spatialContext?.modelId || null;

                      visualCaptureDiagnostic.recordCaptured({
                        sourceId: 'three-model-viewer',
                        modelId,
                        width: res.snapshot.image.width,
                        height: res.snapshot.image.height,
                        encodedBytes: res.snapshot.image.encodedBytes || imgData.length,
                        imageBase64: imgData,
                      });

                      visualCaptureDiagnostic.recordSubmittedToClient({
                        sourceId: 'three-model-viewer',
                        modelId,
                        encodedBytes: imgData.length,
                        clientStatus: clientRef.current.status,
                      });

                      clientRef.current.sendRealtimeInput([
                        {
                          mimeType: 'image/jpeg',
                          data: imgData,
                        },
                      ], { modelId, metadata: { title: res.request?.modelMetadata?.title } });
                    } else if (!res.success) {
                      visualCaptureDiagnostic.recordSkipped({
                        sourceId: 'three-model-viewer',
                        reason: res.skippedReason || 'capture-failed',
                      });
                    }
                  })
                  .catch((err) => {
                    console.warn('[GeminiAvatar] 3D Model visual capture error:', err);
                    visualCaptureDiagnostic.recordError({
                      sourceId: 'three-model-viewer',
                      error: err?.message || String(err),
                    });
                  })
                  .finally(() => {
                    isModelCaptureInFlightRef.current = false;
                  });
              }
            } else {
              // Model viewer is mounted but not yet active (e.g. loading)
              // CRITICAL: Fail closed, NEVER fallback to Krpano 360 while model viewer is mounted!
              visualCaptureDiagnostic.recordSkipped({
                sourceId: 'three-model-viewer',
                reason: 'model-loading',
              });
              // Model loading — still send audio so Gemini can hear the user
              clientRef.current.sendRealtimeInput(chunks);
            }
          } else if (capturerRef.current) {
            // 360 Krpano view: synchronous capture (Phase 1)
            // Skip regular frame capture if full-360 panorama capture is in progress
            // to avoid competing image streams and Gemini API interruptions
            const holdUntil = Number((window as any).__full360HoldUntil) || 0;
            const isFull360Active =
              (window as any).__full360Capturing === true || Date.now() < holdUntil;
            // Only capture when user is speaking or just spoke (3s grace window)
            // AND agent is NOT speaking, NOT thinking, NOT running a tool, and full-360 is NOT running.
            // Sending images while Gemini is thinking or executing tools causes Gemini Live API
            // to interrupt its own response/thinking cycle.
            const isAgentBusy = isAgentSpeakingRef.current || isThinkingRef.current || isToolRunningRef.current || isFull360Active;
            const userSpokeRecently = !isAgentBusy && (audioRecorderRef.current?.spokeRecently(3000) ?? false);
            const capture = userSpokeRecently ? capturerRef.current.captureIfReady() : null;
            if (capture) {
              visualCaptureDiagnostic.recordCaptured({
                sourceId: 'krpano',
                width: 512,
                height: 512,
                encodedBytes: capture.imageBase64.length,
                imageBase64: capture.imageBase64,
              });

              visualCaptureDiagnostic.recordSubmittedToClient({
                sourceId: 'krpano',
                encodedBytes: capture.imageBase64.length,
                clientStatus: clientRef.current?.status,
              });

              chunks.push({
                mimeType: "image/jpeg",
                data: capture.imageBase64,
              });

              // Build spatial context text for hybrid pipeline (Task 1.4)
              // When focused object is present, includes instruction for Gemini
              // to call retrieve_object_knowledge tool before answering
              if (capture.spatialContext) {
                // Build name→title map for readable context
                const titleMap: Record<string, string> = {};
                for (const h of capture.spatialContext.visibleObjects) {
                  if (h.title) titleMap[h.name] = h.title;
                }

                const spatialText = buildKrpanoSpatialContextText({
                  focusedObjectId: capture.spatialContext.focusedObject?.name ?? null,
                  focusedObjectDbId: capture.spatialContext.focusedObject?.dbId ?? null,
                  visibleObjectIds: capture.spatialContext.visibleObjects.map(h => h.name),
                  visibleObjectTitles: titleMap,
                  camera: {
                    hlookat: capture.camera.hlookat,
                    vlookat: capture.camera.vlookat,
                    fov: capture.camera.fov,
                  },
                  sceneId: capture.spatialContext.scene ?? null,
                });
              // Pass spatial context as 3rd param (krpanoContextText)
                clientRef.current.sendRealtimeInput(chunks, undefined, spatialText);
              } else {
                clientRef.current.sendRealtimeInput(chunks);
              }
            } else {
              // No capture ready (rate-limited) — send audio-only chunks
              clientRef.current.sendRealtimeInput(chunks);
            }
          } else {
            // No visual capture source active — send audio-only chunks
            clientRef.current.sendRealtimeInput(chunks);
          }
        }
      });

      audioRecorderRef.current.on("stopped", () => {
        if (clientRef.current?.status === "connected") {
          clientRef.current.startThinking();
        }
      });

      if (enableMic) {
        await audioRecorderRef.current.start();
        if (isChatExpanded) {
          console.log("🔇 [GeminiAvatar] Chat input is active on start - Muting microphone to prevent typing noise");
          audioRecorderRef.current.mute();
        }
      }
      await audioStreamerRef.current.resume();

      // Fetch project name dynamically
      const isProjectMode = !!(idPath || (window as any)?.id_path);
      let projectName = "Starglobal 3D"; // Default for homepage
      if (isProjectMode) {
        try {
          const projectResponse = await functionCallingServices.getProjectNameById(idPath || (window as any)?.id_path);
          if (projectResponse?.data) {
            projectName = projectResponse.data;
          }
        } catch (error) {
          console.error("Error fetching project name:", error);
        }
      }

      // Get current avatar name from dynamic config
      const avatarName = agentConfig.name;
      const sessionLanguage = defaultLanguage;
      const languageDirective = getGeminiLanguageDirective(sessionLanguage);

      const NORTHERN_ACCENT_DIRECTIVE = sessionLanguage === "Vietnamese"
        ? `🎙️ GIỌNG NÓI BẮT BUỘC: Luôn nói tiếng Việt bằng giọng miền Bắc (chuẩn Hà Nội). Sử dụng cách phát âm, từ vựng và ngữ điệu của người Hà Nội. TUYỆT ĐỐI KHÔNG dùng giọng miền Nam hay miền Trung. Ví dụ: dùng "vâng" thay vì "dạ", "anh/chị" tự nhiên theo phong cách miền Bắc.`
        : "";

      let systemInstructionText = "";
      const prefix = [languageDirective, NORTHERN_ACCENT_DIRECTIVE].filter(Boolean).join("\n\n");

      if (isHubMode && hubSystemInstruction) {
        systemInstructionText = `${prefix ? `${prefix}\n\n` : ""}${hubSystemInstruction}`;
      } else if (isProjectMode) {
        systemInstructionText = `${prefix ? `${prefix}\n\n` : ""}${getSystemInstruction(avatarName, projectName, {
          roleOverride: agentConfig.systemPrompt || undefined,
          defaultLanguage: sessionLanguage,
          enableKnowledgeBase: isKnowledgeBaseEnabled,
          enableChatInput: isChatInputEnabled,
        })}\n\n${MODEL_VISUAL_GROUNDING_INSTRUCTION}${agentConfig.enableVisualCapture ? `\n\n${VISUAL_ANALYSIS_INSTRUCTION}` : ''}${agentConfig.enableFull360Capture ? `\n\n${FULL_360_CAPABILITY_INSTRUCTION}` : ''}`;
      } else if (isMapModeRef.current) {
        const defaultMapPersona = `Bạn là ${avatarName} -  Hướng dẫn viên ảo 3D của StarGlobal 3D. Hiện tại người dùng đang xem Bản đồ số hóa 3D (Map 3D).
Nhiệm vụ của bạn là hỗ trợ người dùng tìm kiếm và lọc các dự án cụ thể trên bản đồ theo tỉnh thành, miền hoặc lĩnh vực hoạt động.`;
        
        const mapPersona = agentConfig.systemPrompt || defaultMapPersona;

        const MAP_RULES = `HƯỚNG DẪN QUAN TRỌNG:
1. Khi người dùng yêu cầu lọc/xem các dự án theo miền (Miền Bắc/Miền Trung/Miền Nam), theo tỉnh thành (ví dụ: Hà Nội, Thành phố Hồ Chí Minh, Đà Nẵng...), hoặc theo lĩnh vực (ví dụ: du lịch, di sản, công nghiệp...), bạn BẮT BUỘC phải sử dụng công cụ \`filter_map_projects\` để áp dụng bộ lọc chạy ngầm trực tiếp trên giao diện bản đồ. KHÔNG ĐƯỢC dùng các công cụ điều hướng khác như \`open_sector_project_list\` hay \`open_sector_list\`.
   QUY TẮC PHÂN LOẠI LĨNH VỰC (BẮT BUỘC):
   - Lĩnh vực "dầu khí" BẮT BUỘC phải map thành "oil-and-gas".
   - Lĩnh vực "khu công nghiệp", "công nghiệp" BẮT BUỘC phải map thành "zone" (KHÔNG được tự ý chuyển sang "factory" hay "industrial-zone").
   - Lĩnh vực "hạ tầng" BẮT BUỘC phải map thành "facility" (KHÔNG được tự ý chuyển sang "factory" do viết gần giống nhau).
   - Lĩnh vực "nhà máy" mới được phép map thành "factory".
   - Lĩnh vực "du lịch" map thành "tourism".
   - Lĩnh vực "di sản" map thành "heritage".
   - Lĩnh vực "bảo tàng" map thành "museum".
   - Lĩnh vực "đô thị" hoặc "thành phố" map thành "city".
   - Lĩnh vực "trường học", "giáo dục" map thành "school".
   - Lĩnh vực "trang trại", "nông nghiệp" map thành "farm".
2. Sau khi gọi \`filter_map_projects\`, hãy đọc dữ liệu trả về từ công cụ gồm \`total\` và \`projects\`. Nếu có dự án, hãy trình bày tự nhiên một vài dự án tiêu biểu theo tên, tỉnh/thành hoặc lĩnh vực; đừng chỉ nói rằng đã mở bộ lọc. Nếu không có dự án phù hợp, hãy nói rõ chưa tìm thấy và gợi ý người dùng thử tỉnh/lĩnh vực khác.
3. Nếu người dùng muốn xem, tìm kiếm hoặc muốn chuyển đến một dự án cụ thể theo tên (ví dụ: "Petro Việt Nam/ Khu công viên phần mềm Quang Trung"), bạn BẮT BUỘC phải gọi công cụ \`get_projects_by_location\` với từ khóa tìm kiếm truyền vào tham số \`search\` để tra cứu thông tin dự án và lấy được đúng \`url\` của nó. Sau đó, gọi công cụ \`focus_map_project\` với đúng \`project_url\` vừa lấy được để zoom bản đồ tới vị trí dự án và hiển thị popup thông tin của dự án (KHÔNG được tự động kích hoạt chỉ đường).
4. Chỉ gọi công cụ \`open_map_directions\` khi người dùng hỏi rõ về chỉ đường, hướng dẫn đường đi, tìm đường đi tới dự án. Khi đang ở chế độ dẫn đường, nếu người dùng muốn tắt chỉ đường, đóng chỉ đường, dừng chỉ đường hoặc quay lại bản đồ từ chế độ chỉ đường, bạn BẮT BUỘC phải gọi công cụ \`close_map_directions\` để thoát chế độ chỉ đường và quay lại màn hình bản đồ thông thường. TUYỆT ĐỐI KHÔNG được gọi công cụ \`close_map\` để tắt chỉ đường vì nó sẽ đóng toàn bộ bản đồ số hóa.
5. Sau khi đã đưa người dùng tới xem dự án cụ thể (thông qua \`focus_map_project\`), hãy hỏi người dùng xem chi tiết hoặc hướng dẫn đường đi bằng đúng ngôn ngữ họ đang sử dụng:
   - Tiếng Việt: "Em đã đưa anh/chị đến dự án [Tên dự án]. Anh/chị có muốn xem chi tiết dự án hay xem hướng dẫn đường đi đến vị trí dự án không?"
   - Tiếng Anh: "I have taken you to [Project Name]. Would you like to view its details or get directions to the project location?"
6. Luôn trả lời bằng đúng ngôn ngữ mà người dùng đang sử dụng trong câu gần nhất, không tự đổi ngôn ngữ.
7. Khi người dùng yêu cầu đổi bản đồ hoặc chuyển chế độ hiển thị bản đồ giữa 34 tỉnh/thành và 63 tỉnh/thành (ví dụ: 'chuyển sang bản đồ 63 tỉnh', 'hiển thị 34 tỉnh'), bạn BẮT BUỘC phải gọi công cụ \`switch_province_mode\` với đúng giá trị của \`mode\` ('34' hoặc '63'). TUYỆT ĐỐI KHÔNG tự ý gọi công cụ này khi người dùng chỉ yêu cầu quay lại toàn cảnh, reset bản đồ hiện tại hoặc hủy bộ lọc của bản đồ hiện tại; chỉ thay đổi bản đồ khi người dùng chỉ định rõ ràng việc đổi bản đồ.
8. LƯU Ý KHI ĐANG Ở BẢN ĐỒ 34 TỈNH: 
   Nếu người dùng yêu cầu lọc/xem một tỉnh không nằm trong danh sách 34 tỉnh (ví dụ: Bến Tre, Bình Định, Bình Phước, Quảng Nam, Kiên Giang, Bạc Liêu...), bạn phải kiểm tra thông tin phản hồi từ công cụ \`filter_map_projects\`. Nếu công cụ báo tỉnh đó không thuộc 34 tỉnh do đã được sáp nhập (ví dụ: Bến Tre đã gộp vào Vĩnh Long, Bình Định gộp vào Gia Lai, Bình Phước gộp vào Đồng Nai, Quảng Nam gộp vào Đà Nẵng, Kiên Giang gộp vào An Giang, Bạc Liêu gộp vào Cà Mau...), hãy giải thích lịch sự cho người dùng hiểu rằng tỉnh đó hiện được gộp chung vào tỉnh mới tương ứng trên bản đồ 34 tỉnh này. Đồng thời, gợi ý ngay cho người dùng chuyển sang chế độ bản đồ 63 tỉnh để xem riêng tỉnh đó (sử dụng công cụ \`switch_province_mode\` với \`mode\` là '63').
9. Khi người dùng yêu cầu đặt lại bộ lọc, quay lại xem toàn cảnh bản đồ hiện tại, hủy bộ lọc đang áp dụng, muốn xem các dự án khác hoặc khi họ nói bất kỳ câu nói nào thể hiện mong muốn quay lại trạng thái ban đầu của bản đồ hiện tại (ví dụ: 'quay lại', 'quay lại toàn cảnh', 'hủy bộ lọc', 'cho xem dự án khác', 'xem lại tất cả dự án', 'reset bản đồ'), bạn BẮT BUỘC phải gọi công cụ \`reset_map_filter\` để đưa bản đồ hiện tại và bộ lọc về trạng thái mặc định ban đầu. TUYỆT ĐỐI KHÔNG gọi công cụ \`close_map\` (vốn để đóng toàn bộ bản đồ số hóa) hay công cụ \`switch_province_mode\` (vốn để đổi sang bản đồ khác) khi người dùng chỉ có ý định quay lại toàn cảnh của bản đồ hiện tại. Nếu người dùng chỉ muốn tắt dẫn đường/chỉ đường, hãy gọi công cụ \`close_map_directions\`.
10. Khi người dùng muốn xem/lọc các dự án ở Việt Nam, trong nước hoặc trong lãnh thổ Việt Nam (ví dụ: 'dự án du lịch ở Việt Nam', 'các dự án ở Việt Nam'), bạn BẮT BUỘC phải truyền giá trị "Việt Nam" vào tham số \`country\` của công cụ \`filter_map_projects\`. Khi người dùng yêu cầu lọc/xem các dự án ở nước ngoài, ngoài nước, hoặc quốc tế (ví dụ: 'dự án ở nước ngoài', 'các dự án ngoài nước'), bạn BẮT BUỘC phải truyền giá trị 'nước ngoài' hoặc 'abroad' vào tham số \`country\`. Nếu họ yêu cầu nước cụ thể như Mỹ, Canada, Úc, hãy truyền tên nước tương ứng.
Hãy trả lời lịch sự, tự nhiên, hướng dẫn người dùng nhìn vào bản đồ để thấy các thay đổi trực quan.`;

        systemInstructionText = `${prefix ? `${prefix}\n\n` : ""}${mapPersona}\n\n${MAP_RULES}`;
      } else {
        const defaultHomepagePersona = `Bạn là ${avatarName} - Hướng dẫn viên ảo 3D của StarGlobal 3D.
Nhiệm vụ của bạn là hỗ trợ và tư vấn giải pháp cho khách hàng, giới thiệu các giải pháp công nghệ nổi bật của Starglobal 3D như: chuyển đổi số không gian 3D/360, bản đồ số 3D, và các dự án tiêu biểu.
Hãy nói chuyện nhẹ nhàng, tự nhiên, tự tin và truyền cảm hứng về công nghệ Việt. Khi người dùng hỏi thăm quan hoặc gợi ý địa điểm, hãy nhiệt tình giới thiệu các giải pháp nổi bật này.`;

        const homepagePersona = agentConfig.systemPrompt || defaultHomepagePersona;

        const HOMEPAGE_RULES = `ĐẶC BIỆT: Khi người dùng yêu cầu mở bản đồ, xem bản đồ số hóa, bản đồ 3D nói chung, bạn BẮT BUỘC phải gọi công cụ \`open_map\`. Chỉ gọi công cụ \`open_map_directions\` khi người dùng yêu cầu hướng dẫn đường đi, chỉ đường hoặc tìm đường đi tới một địa điểm/dự án cụ thể.`;

        systemInstructionText = `${prefix ? `${prefix}\n\n` : ""}${homepagePersona}\n\n${HOMEPAGE_RULES}`;
      }

      const baseTools = (isHubMode && hubToolsProp)
        ? hubToolsProp
        : isMapModeRef.current
          ? mapModeTools
          : (isProjectMode ? tools : portalTools);

      let selectedTools = isKnowledgeBaseEnabled
        ? baseTools
        : Object.fromEntries(Object.entries(baseTools).filter(([k]) => 
            k !== 'search_project_knowledge' && 
            k !== 'get_search_result' && 
            k !== 'search_whitelist_web'
          ));

      // Only include capture_full_scene tool when full 360° capture is enabled in admin
      if (!agentConfig.enableFull360Capture) {
        selectedTools = Object.fromEntries(
          Object.entries(selectedTools).filter(([k]) => k !== 'capture_full_scene')
        );
      }

      // Connect to Gemini with comprehensive instructions
      // Capitalize first letter to match Gemini API format (e.g. "marin" → "Marin")
      const validGeminiVoices = ["Aoede", "Charon", "Fenrir", "Kore", "Puck", "Leda", "Zephyr", "Orus"];
      let geminiVoiceName = 'Aoede';
      if (agentConfig.voice) {
        const capitalized = agentConfig.voice.charAt(0).toUpperCase() + agentConfig.voice.slice(1);
        if (validGeminiVoices.includes(capitalized)) {
          geminiVoiceName = capitalized;
        } else {
          console.warn(`Gemini Avatar: Voice "${agentConfig.voice}" (capitalized: "${capitalized}") is not supported by Gemini Live API. Falling back to "Aoede".`);
        }
      }

      const connected = await clientRef.current.connect(MODEL, {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: {
            prebuiltVoiceConfig: {
              voiceName: geminiVoiceName,
            },
          },
        },
        maxOutputTokens: 8192,
        tools: [
          ...(isKnowledgeBaseEnabled ? [] : [{ googleSearch: {} }]),
          {
            functionDeclarations: Object.values(selectedTools).map(t => ({
              ...t.declaration,
              parameters: {
                ...t.declaration.parameters,
                type: Type.OBJECT,
                properties: t.declaration.parameters.properties
              }
            }))
          }
        ],
        systemInstruction: {
          parts: [{
            text: systemInstructionText
          }]
        }
      });

      if (!connected) {
        setStatus("Failed to connect");
        setIsConnecting(false);
        setIsActive(false);
        audioRecorderRef.current?.stop();
        audioStreamerRef.current?.stop();
        return;
      }

      setStatus("Active - Listening...");
    } catch (error: any) {
      console.error("Error starting voice:", error);
      setStatus(`Error: ${error.message}`);
      setIsActive(false);
      setIsConnecting(false);
      audioRecorderRef.current?.stop();
      audioStreamerRef.current?.stop();
    }
  };

  /**
   * Pause the active session: mute microphone, silence agent audio output,
   * but keep the WebSocket session alive so the conversation context is
   * preserved and can be resumed without re-greeting.
   */
  const pauseSession = () => {
    if (!isActive || isPaused) return;

    // Mute mic input (keeps stream alive for resume)
    audioRecorderRef.current?.pause();

    // Silence agent audio output and clear any queued playback
    audioStreamerRef.current?.stop();

    // Clear viseme/animation so avatar returns to idle
    setVisemeOverride(undefined);
    setIsThinking(false);

    setIsPaused(true);
    setStatus("Paused");
    console.log("⏸️ Session paused — mic muted, session alive");
  };

  /**
   * Resume a paused session: re-enable mic and audio output.
   * The WebSocket session was never disconnected so the agent continues
   * from where the conversation left off.
   */
  const resumeSession = async () => {
    if (!isActive || !isPaused) return;

    // Re-enable mic input
    audioRecorderRef.current?.unpause();

    // Resume audio context so agent responses can play again
    await audioStreamerRef.current?.resume();

    setIsPaused(false);
    setStatus("Active - Listening...");
    console.log("▶️ Session resumed — mic re-enabled");
  };

  const toggleVoice = async () => {
    if (!clientRef.current || !audioStreamerRef.current) {
      setStatus("Error: Client not initialized");
      return;
    }

    if (isActive && isPaused) {
      // Currently paused → Resume the session
      if (!audioRecorderRef.current?.stream) {
        await audioRecorderRef.current?.start();
      }
      await resumeSession();
    } else if (isActive) {
      // If active in text-only mode (mic not started yet), start mic now
      if (!audioRecorderRef.current?.stream) {
        await audioRecorderRef.current?.start();
        setIsPaused(false);
        setStatus("Active - Listening...");
      } else {
        // Currently active with mic → Pause the session
        pauseSession();
      }
    } else {
      // Currently disconnected → Start new connection with mic
      isManualDisconnectRef.current = false;
      setIsPaused(false);
      await startVoice(false, true);
    }
  };

  /**
   * Full disconnect: tears down mic, audio, and WebSocket regardless
   * of whether the session is active or paused.
   */
  const disconnectSession = () => {
    isManualDisconnectRef.current = true;
    setStatus("Disconnecting...");
    audioRecorderRef.current?.stop();
    audioStreamerRef.current?.stop();
    clientRef.current?.disconnect();
    // hotspotHighlighter.clear();
    setIsActive(false);
    setIsConnecting(false);
    setIsThinking(false);
    setIsPaused(false);
    setStatus("Disconnected");
    setVisemeOverride(undefined);
  };

  const handleSendTextMessage = async (messageText: string) => {
    const text = messageText.trim();
    if (!text) return;

    // Instant Text Barge-In: Cut AI speech & lipsync immediately like speaking directly!
    audioStreamerRef.current?.stop();
    setVisemeOverride(undefined);

    if (isActive && isPaused) {
      // Typing a message while paused wakes the agent; mic stays muted by the chat input
      await resumeSession();
    } else if (!isActive && !isConnecting) {
      isManualDisconnectRef.current = false;
      await startVoice(false, false);
    }

    setIsThinking(true);
    setStatus("Thinking...");

    if (clientRef.current?.status === "connected") {
      clientRef.current.startThinking();
      clientRef.current.send([
        { text }
      ], true);
    } else {
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (clientRef.current?.status === "connected") {
          clearInterval(interval);
          clientRef.current.startThinking();
          clientRef.current.send([
            { text }
          ], true);
        } else if (attempts > 20) {
          clearInterval(interval);
        }
      }, 250);
    }
  };

  useEffect(() => {
    if (isActive && lastActiveMapModeRef.current !== isMapMode) {
      console.log(`🔄 Map mode changed from ${lastActiveMapModeRef.current} to ${isMapMode}. Reconnecting session...`);
      lastActiveMapModeRef.current = isMapMode;
      isReconnectingRef.current = true;
      startVoice(true);
    } else {
      lastActiveMapModeRef.current = isMapMode;
    }
  }, [isMapMode, isActive]);

  useEffect(() => {
    if (isActive && lastActiveDefaultLanguageRef.current !== defaultLanguage) {
      console.log(`🔄 Agent language changed from ${lastActiveDefaultLanguageRef.current} to ${defaultLanguage}. Reconnecting session...`);
      lastActiveDefaultLanguageRef.current = defaultLanguage;
      isReconnectingRef.current = true;
      startVoice(true);
    } else {
      lastActiveDefaultLanguageRef.current = defaultLanguage;
    }
  }, [defaultLanguage, isActive]);

  // Pause/mute microphone recording when chat input is opened/typing to prevent keyboard & ambient noise
  useEffect(() => {
    if (!audioRecorderRef.current) return;
    if (isChatExpanded) {
      console.log("🔇 [GeminiAvatar] Chat input is active - Muting microphone to prevent typing noise/clatter");
      audioRecorderRef.current.mute();
    } else if (isActive) {
      // unmute() leaves the mic off if the session is paused
      console.log("🎙️ [GeminiAvatar] Chat input closed - Restoring microphone listening");
      audioRecorderRef.current.unmute();
    }
  }, [isChatExpanded, isActive]);

  // Animate avatar lipsync and posture while natural voice filler speech is active
  useEffect(() => {
    let animTimer: NodeJS.Timeout | null = null;
    const handleFillerSpeaking = (e: Event) => {
      const detail = (e as CustomEvent)?.detail;
      if (detail?.isSpeaking) {
        if (talkingDebounceTimerRef.current) {
          clearTimeout(talkingDebounceTimerRef.current);
          talkingDebounceTimerRef.current = null;
        }
        setIsAvatarTalking(true);
        setVisemeOverride("viseme_aa");
        animTimer = setInterval(() => {
          setVisemeOverride(prev => prev === "viseme_aa" ? "viseme_O" : "viseme_aa");
        }, 180);
      } else {
        if (animTimer) clearInterval(animTimer);
        animTimer = null;
        setVisemeOverride(undefined);
        if (!talkingDebounceTimerRef.current) {
          talkingDebounceTimerRef.current = setTimeout(() => {
            setIsAvatarTalking(false);
            talkingDebounceTimerRef.current = null;
          }, 600);
        }
      }
    };
    window.addEventListener('sg3d:voice-filler-speaking', handleFillerSpeaking);
    return () => {
      window.removeEventListener('sg3d:voice-filler-speaking', handleFillerSpeaking);
      if (animTimer) clearInterval(animTimer);
    };
  }, []);

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Title Only - Top Left */}
      {/* <div style={{
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        background: 'rgba(0,0,0,0.5)',
        padding: '20px',
        borderRadius: '12px',
        color: 'white',
        backdropFilter: 'blur(5px)'
      }}>
        <h1 className="text-xl font-bold">Gemini Live Avatar</h1>

        <select
          value={selectedAvatarIndex}
          onChange={(e) => setSelectedAvatarIndex(Number(e.target.value))}
          disabled={isActive}
          style={{
            marginTop: '12px',
            padding: '8px 12px',
            borderRadius: '8px',
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'rgba(255,255,255,0.1)',
            color: 'white',
            fontSize: '14px',
            cursor: isActive ? 'not-allowed' : 'pointer',
            opacity: isActive ? 0.5 : 1,
          }}
        >
          {AVATARS.map((avatar, index) => (
            <option key={avatar.id} value={index} style={{ background: '#1f2937', color: 'white' }}>
              {avatar.name}
            </option>
          ))}
        </select>
      </div> */}

      {/* Circular Button - Center Bottom (hidden while the chat input is expanded) */}
      {!isChatExpanded && (
        <div style={{
          position: 'absolute',
          bottom: 10,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {/* Main call/pause/resume button */}
            <button
              onClick={toggleVoice}
              disabled={isConnecting}
              style={{
                background: isPaused ? '#faad14' : (isActive ? '#ff4d4f' : '#52c41a'),
                border: `2px solid ${isPaused ? '#faad14' : (isActive ? '#ff4d4f' : '#52c41a')}`,
                borderRadius: '50%',
                padding: 0,
                cursor: isConnecting ? 'not-allowed' : 'pointer',
                opacity: isConnecting ? 0.6 : 1,
                transition: 'all 0.3s ease',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '20px',
                boxShadow: isPaused
                  ? '0 0 12px 3px rgba(250, 173, 20, 0.4)'
                  : '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                animation: isPaused ? 'pulse-pause 2s ease-in-out infinite' : 'none',
              }}
              title={
                isPaused
                  ? 'Tiếp tục cuộc trò chuyện'
                  : isActive
                    ? 'Tạm dừng cuộc trò chuyện'
                    : 'Kết nối với Agent'
              }
            >
              {isConnecting
                ? '⏳'
                : isPaused
                  ? <FaPlay style={{ marginLeft: '2px' }} />
                  : isActive
                    ? (isThinking ? <FaHourglassHalf className="animate-spin" /> : <FaPause />)
                    : <FaPhone />
              }
            </button>

            {isAiInteractionRecordingEnabled && <AgentRecordButton size={48} />}

            {/* Close agent panel button */}
            {handleClose && (
              <button
                onClick={() => {
                  // If active or paused, disconnect first before closing
                  if (isActive) disconnectSession();
                  handleClose();
                }}
                style={{
                  background: '#ff4d4f',
                  border: '2px solid #ff4d4f',
                  borderRadius: '50%',
                  padding: 0,
                  color: 'white',
                  cursor: 'pointer',
                  width: '48px',
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                }}
                title="Tắt Agent"
              >
                <FaTimes style={{ fontSize: '18px' }} />
              </button>
            )}
          </div>

          {!apiKey && (
            <p style={{ marginTop: '8px', fontSize: '14px', color: '#fde047', textAlign: 'center' }}>
              ⚠️ Missing API Key
            </p>
          )}
        </div>
      )}

      {isLoadingAgent ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm rounded-2xl border border-white/10" style={{ minHeight: '300px' }}>
          <div className="w-8 h-8 rounded-full border-4 border-white/20 border-t-[#3b82f6] animate-spin mb-2" />
          <span className="text-white text-xs font-semibold select-none">Đang tải cấu hình Avatar...</span>
        </div>
      ) : isPreloaded ? (
        <AvatarPlayer
          lowPower={lowPower}
          paused={avatarPaused}
          skinnedMesh={resolvedSkinnedMesh}
          modelPath={activeModelPath}
          animationPath={activeAnimationPath}
          visemeOverride={visemeOverride}
          emotion={isAvatarTalking ? "smile" : "default"}
          animation={isThinking ? "Thinking" : (isAvatarTalking ? talkingAnimation : "Idle")}
          cameraDefault={[0, 1.5, 4.5, 0, 0.7, 0, true]}
          minPolarAngle={Math.atan2(4.5, 0.8)}
          maxPolarAngle={Math.atan2(4.5, 0.8)}
          minDistance={2.5}
          maxDistance={6.5}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-black/40 backdrop-blur-sm rounded-2xl border border-white/10" style={{ minHeight: '300px' }}>
          <div className="w-8 h-8 rounded-full border-4 border-white/20 border-t-[#3b82f6] animate-spin mb-2" />
          <span className="text-white text-xs font-semibold select-none">Đang tải trợ lý 3D...</span>
        </div>
      )}

      <AgentKnowledgeCheckpointHud />

      {isChatInputEnabled && (
        <AgentChatInput
          onSendMessage={handleSendTextMessage}
          isLoading={isThinking}
          isThinking={isThinking}
          isActive={isActive}
          isConnecting={isConnecting}
          isPaused={isPaused}
          onToggleVoice={toggleVoice}
          onExpandedChange={setIsChatExpanded}
        />
      )}

      <RecommendationPopup />
      <FacilityDemoOverlay />
    </div>
  );
}
